import {
  AsYouType,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";

/**
 * Normalize a phone number to E.164 format (e.g. "+819036502962").
 *
 * Consistent normalization is required for phone-based dedup checks:
 * "+81-90-3650-2962", "+81 90 3650 2962" and "+819036502962" all refer to
 * the same real number but hash to different values unless normalized first.
 *
 * @param raw       User-entered phone string
 * @param defaultCountry  ISO 3166-1 alpha-2 country code (e.g. "KR", "JP").
 *                        Used only when `raw` is a national number without
 *                        country prefix; ignored if `raw` already starts with "+".
 * @returns  E.164 formatted string (e.g. "+819036502962") or null if invalid.
 */
export function normalizeToE164(
  raw: string,
  defaultCountry?: string,
): string | null {
  if (!raw) return null;
  const parsed = parsePhoneNumberFromString(raw, defaultCountry as CountryCode | undefined);
  if (!parsed || !parsed.isValid()) return null;
  return parsed.number; // e.g. "+819036502962"
}

/**
 * Whether an E.164 string has the shape of a Korean mobile number
 * (`+8210` + 8 digits) — the only shape a phone stored for a KR customer who
 * passed mobile identity verification should have.
 *
 * This checks shape, and on the Danal path shape is what makes ownership hold.
 * PortOne returns no phone number for Danal without an add-on contract
 * (verifiedCustomer carries birthDate, ci, di, gender, id, name), so the
 * number stored is the one the customer typed. Callers pass that same number
 * as `customer.phoneNumber`, which locks Danal's window to it — but only a
 * well-formed number can be locked. A malformed one is not, the customer
 * verifies with their real number in Danal's window, and the malformed input
 * is what gets stored.
 *
 * `normalizeToE164` is not enough on that path. It asks libphonenumber
 * "is this a real number anywhere in KR", which accepts Seoul landlines
 * (`+8221234567`) and legacy 10-digit mobiles (`+82101234567`). A mobile
 * number that lost digits can land on either shape and pass. Production
 * collected 11 such rows before this contract existed: 9 where the customer
 * saw the `+82` chip and typed only the 8 digits after `010`, and 2 off by a
 * single digit. Both survived `isValid()`.
 *
 * Deliberately not generalized to other countries: the default
 * libphonenumber metadata ships no number-type data, so `getType()` returns
 * undefined and "is this a mobile" cannot be asked outside a hand-written
 * per-country pattern.
 */
export function isKoreanMobileE164(e164: string | null | undefined): boolean {
  if (!e164) return false;
  return /^\+8210\d{8}$/.test(e164);
}

/**
 * Whether an E.164 string has the shape of a mainland China mobile number
 * (`+86` + `1[3-9]` + 9 digits).
 *
 * China goes through manual admin approval instead of SMS (our provider
 * cannot deliver to +86 — see lib/verification/manual-phone.ts), so nothing
 * proves the number belongs to the customer. Narrowing it to a mobile shape
 * is the least we can check. `normalizeToE164` alone accepts landlines such as
 * Beijing `+861012345678`, the same trap as Korean landlines above.
 */
export function isChineseMobileE164(e164: string | null | undefined): boolean {
  if (!e164) return false;
  return /^\+861[3-9]\d{9}$/.test(e164);
}

/**
 * Group an in-progress phone input the way its country writes it
 * (`01012345678` → `010-1234-5678`).
 *
 * The input used to render the raw digit run it stored, and `01012345678`
 * against `0101234567` is indistinguishable at a glance — customers had no
 * way to catch their own missing digit. Grouping breaks visibly when a digit
 * is missing (`010-123-4567`), and a number typed without `010` gets no
 * grouping at all, which is itself the signal.
 *
 * Partial input is returned as-is rather than rejected, so the caret does
 * not jump while someone is still typing.
 */
export function formatPhoneAsYouType(
  digits: string,
  defaultCountry?: string,
): string {
  if (!digits) return "";
  return new AsYouType(defaultCountry as CountryCode | undefined).input(digits);
}

/**
 * Whether the input parses to a real, dialable number.
 *
 * Replaces country-specific prefix allowlists (the old `validateKoreanPhone`
 * only accepted 010/011/016-019, which rejected every Japanese customer —
 * and +81 is this product's largest customer segment, ahead of +82).
 */
export function isValidPhone(
  raw: string | null | undefined,
  defaultCountry?: string,
): boolean {
  if (!raw) return false;
  return normalizeToE164(raw, defaultCountry) !== null;
}

/**
 * Format a stored E.164 number for display.
 *
 * Storage stays E.164 with no separators; readable grouping is produced at
 * render time only. Unparseable input is returned unchanged so legacy or
 * partially-entered values still render instead of vanishing.
 */
export function formatPhoneForDisplay(
  e164: string | null | undefined,
  style: "national" | "international" = "international",
): string {
  if (!e164) return "";
  const parsed = parsePhoneNumberFromString(e164);
  if (!parsed) return e164;
  return style === "national"
    ? parsed.formatNational()
    : parsed.formatInternational();
}

/**
 * Build a `tel:` URI that actually dials.
 *
 * RFC 3966 wants the global-number form (leading `+`, digits only). Passing a
 * raw DB value straight into `tel:` leaks hyphens and spaces, which some
 * dialers refuse. Returns `""` when nothing dialable can be derived, so the
 * caller can render plain text rather than a dead link.
 */
export function telHref(raw: string | null | undefined): string {
  const normalized = normalizeToE164(raw ?? "");
  if (normalized) return `tel:${normalized}`;
  if (!raw) return "";
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";
  return `tel:${trimmed.startsWith("+") ? "+" : ""}${digits}`;
}

/**
 * Convert an E.164 Korean number to the domestic form SendOn expects
 * (`+821012345678` → `01012345678`). Non-Korean or already-domestic values are
 * returned with separators stripped only.
 */
export function toKoreanDomestic(raw: string): string {
  const digits = raw.replace(/[^0-9+]/g, "");
  const bare = digits.startsWith("+") ? digits.slice(1) : digits;
  if (bare.startsWith("82")) return `0${bare.slice(2)}`;
  return bare;
}

/**
 * Mask a stored phone number for `phone_display` (e.g. `+65 92****1818`).
 *
 * The calling code comes from libphonenumber, not from a guessed length:
 * calling codes are 1–3 digits with no shape to tell them apart (`+7`, `+65`,
 * `+673`). A hard-coded list of two-digit codes used to cut every other
 * country one digit too long (`+659 22****1818`, `+777 …`). Calling codes are
 * prefix-free, so the code is exact even when the rest of the number is not a
 * valid number.
 *
 * Korean numbers keep their historical `+82 10-****-5678` shape. Input without
 * a `+` is treated as a national number and only has its middle masked.
 */
export function maskPhone(phone: string): string {
  if (!phone || phone.length < 8) return "****";

  if (!phone.startsWith("+")) {
    return phone.length >= 8
      ? `${phone.slice(0, 3)}-****-${phone.slice(-4)}`
      : `****${phone.slice(-4)}`;
  }

  const parsed = parsePhoneNumberFromString(phone);
  if (!parsed) return `****${phone.slice(-4)}`;

  const countryCode = `+${parsed.countryCallingCode}`;
  const local = String(parsed.nationalNumber);

  if (countryCode === "+82") {
    return local.length >= 8
      ? `${countryCode} ${local.slice(0, 2)}-****-${local.slice(-4)}`
      : `${countryCode} ****${local.slice(-4)}`;
  }

  return local.length >= 6
    ? `${countryCode} ${local.slice(0, 2)}****${local.slice(-4)}`
    : `${countryCode} ****`;
}
